import React from "react";
import FormHelperText from "@mui/material/FormHelperText";
import FormLabel from "@mui/material/FormLabel";
import Box from "@mui/material/Box";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import type { SelectProps } from "@mui/material/Select";

import SVG from "@/assets/svg";
import { SelectOptions } from ".";

// interface ISelectInput extends SelectProps<string> {
//   placeholder?: string;
//   label?: string;
//   options: SelectOptions[];
//   sx?: SelectProps["sx"];
//   value?: string;
//   errorText?: any;
// }
type ISelectInput = SelectProps<any> & {
  placeholder?: string;
  label?: string;
  options: SelectOptions[];
  sx?: SelectProps["sx"];
  value?: any;
  errorText?: any;
  labelSx?: any;
  required?: boolean;
};

function SelectInputComponent({
  placeholder,
  label,
  options,
  sx,
  value,
  errorText,
  labelSx,
  labelId,
  id,
  name,
  inputProps,
  required,
  ...rest
}: ISelectInput) {
  const [open, setOpen] = React.useState(false);
  const generatedId = React.useId();
  const selectId = id || `select-${generatedId}`;
  const accessibleLabelId = labelId || `${selectId}-label`;
  const nativeInputId = inputProps?.id || `${selectId}-input`;
  const nativeInputName = inputProps?.name || name || selectId;

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  return (
    <Box
      data-testid="select-input-field"
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        width: "100%",
      }}
    >
      {label && (
        <FormLabel
          id={accessibleLabelId}
          sx={{
            color: "#1F4970",
            fontSize: "14px",
            fontWeight: "600",
            padding: "0px 0px 8px 14px",
            "& .MuiSelect-select.Mui-disabled": {
              WebkitTextFillColor: "#000000 !important",
              color: "#000000 !important",
            },
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "#000000 !important",
              color: "#000000 !important",
            },
            textTransform: "capitalize",
            ...labelSx,
          }}
          htmlFor={nativeInputId}
        >
          {label}
        </FormLabel>
      )}

      <Select
        labelId={labelId || (label ? accessibleLabelId : undefined)}
        sx={[
          {
            borderRadius: "16px",
            height: "40px",
            fontSize: "0.875rem",
            fontWeight: "400",
            width: "100%",
            position: "relative",
            "& .MuiSelect-select.Mui-disabled": {
              WebkitTextFillColor: "#000000 !important",
              color: "#000000 !important",
            },
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "#000000 !important",
              color: "#000000 !important",
            },
            ...(errorText && {
              border: "1px solid #FF0000",
            }),
          },
          {
            color: errorText ? "#FA896B" : "#000000",
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        IconComponent={() => (
          <SVG.DownArrow
            onClick={handleOpen}
            width="16px"
            style={{
              paddingRight: "10px",
              zIndex: "0",
              position: "absolute",
              right: " 0",
              cursor: "pointer",
            }}
          />
        )}
        displayEmpty
        value={value}
        renderValue={
          value && (Array.isArray(value) ? value.length > 0 : true)
            ? undefined
            : () => <div>{placeholder}</div>
        }
        {...rest}
        id={selectId}
        name={nativeInputName}
        inputProps={{
          "aria-label":
            rest["aria-label"] ??
            (!label && !labelId && !rest["aria-labelledby"]
              ? placeholder
              : undefined),
          ...inputProps,
          id: nativeInputId,
          name: nativeInputName,
          required: false,
        }}
        data-testid={label}
      >
        {options.map((option, i) => {
          return option?.isGroupTitle ? (
            <ListSubheader key={i}>{option.label}</ListSubheader>
          ) : (
            <MenuItem
              value={option.value}
              disabled={Boolean(option?.disabled)}
              key={i}
            >
              {option.label}
            </MenuItem>
          );
        })}
      </Select>
      {errorText && (
        <FormHelperText
          error
          sx={{
            fontSize: "12px",
            fontWeight: "400",
            lineHeight: "18px",
            marginTop: "4px",
            marginLeft: "14px",
          }}
        >
          {errorText}
        </FormHelperText>
      )}
    </Box>
  );
}

export default SelectInputComponent;
